import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ListChecks, Filter, X, Search, Plus, ChevronDown, ChevronRight } from 'lucide-react';
import { tasksApi, plantsApi } from '../services/gardenApi';
import BottomSheet from '../components/BottomSheet';
import PrimaryButton from '../components/PrimaryButton';
import TaskRow from '../components/TaskRow';
import { useGardenPageTitle } from '../useGardenPageTitle';
import { displayBotanicalSubtitle, displayPrimaryPlantName } from '../utils/plantDisplayName';

const MONTH_NAMES = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];

const PRESETS = [
  { id: 'this_month', label: 'This month' },
  { id: 'next_3m',    label: 'Next 3 months' },
  { id: 'year',       label: 'Year' },
];

const NOW_YEAR_MONTH = (() => {
  const d = new Date();
  return { year: d.getFullYear(), month: d.getMonth() + 1 };
})();

const PLANT_FILTER_KEY = 'garden.tasks.plantFilter';

function loadStoredPlantFilter() {
  try {
    const raw = localStorage.getItem(PLANT_FILTER_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.filter((x) => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

function savePlantFilter(ids) {
  try {
    if (!ids || ids.length === 0) localStorage.removeItem(PLANT_FILTER_KEY);
    else localStorage.setItem(PLANT_FILTER_KEY, JSON.stringify(ids));
  } catch { /* storage blocked: tolerate */ }
}

export default function TasksScreen() {
  useGardenPageTitle('Tasks', 'Tasks');
  const navigate = useNavigate();
  const [preset, setPreset] = useState('this_month');
  // 'all' shows informational events (bloom, dormancy) alongside action tasks;
  // 'todos' hides them for a pure checklist view.
  const [showMode, setShowMode] = useState('all');
  const [data, setData] = useState({ tasks: [], open_count: 0, completed_count: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pending, setPending] = useState({});

  // Plant focus filter.
  const [plants, setPlants] = useState([]);
  const [selectedPlantIds, setSelectedPlantIds] = useState(() => loadStoredPlantFilter());
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);

  // Custom task editor state. `editor.mode` is 'create' or 'edit'.
  const [editor, setEditor] = useState(null);

  // Completed-section expand/collapse. Collapsed by default so the screen
  // only shows open to-dos on load.
  const [doneOpen, setDoneOpen] = useState(false);

  // Persist the user's filter so tabbing away and back keeps the focus.
  useEffect(() => { savePlantFilter(selectedPlantIds); }, [selectedPlantIds]);

  // Fetch plants once for the filter sheet + custom-task "link to plant"
  // dropdown. Silent failure leaves both empty-but-functional.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await plantsApi.list();
        if (!cancelled) setPlants(r.plants || []);
      } catch {
        if (!cancelled) setPlants([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Prune stale IDs (plants the user deleted) once plants are loaded.
  useEffect(() => {
    if (plants.length === 0 || selectedPlantIds.length === 0) return;
    const live = new Set(plants.map((p) => p.id));
    const pruned = selectedPlantIds.filter((id) => live.has(id));
    if (pruned.length !== selectedPlantIds.length) setSelectedPlantIds(pruned);
  }, [plants, selectedPlantIds]);

  const load = useCallback(async (p = preset) => {
    setLoading(true);
    setError(null);
    try {
      const r = await tasksApi.list(p);
      setData(r);
    } catch (e) {
      setError(e.message || 'Failed to load tasks');
    } finally {
      setLoading(false);
    }
  }, [preset]);

  useEffect(() => { load(preset); }, [load, preset]);

  const filterActive = selectedPlantIds.length > 0;

  // Apply plant focus filter + "to-dos only" filter before grouping.
  // Note: custom tasks can have a null plant_id (not tied to a plant). We
  // include those regardless of the per-plant filter — they're user-authored
  // reminders that shouldn't be filtered out by a plant selection.
  const visibleTasks = useMemo(() => {
    let list = data.tasks;
    if (filterActive) {
      const set = new Set(selectedPlantIds);
      list = list.filter((t) => (t.is_custom && !t.plant_id) || set.has(t.plant_id));
    }
    if (showMode === 'todos') list = list.filter((t) => !t.is_informational);
    return list;
  }, [data.tasks, showMode, selectedPlantIds, filterActive]);

  // Split open vs completed. Completed go into the collapsible "Done" section.
  const openTasks = useMemo(
    () => visibleTasks.filter((t) => !t.completed),
    [visibleTasks]
  );
  const doneTasks = useMemo(
    () => visibleTasks.filter((t) => t.completed),
    [visibleTasks]
  );

  // When filtering, recompute counts so header copy matches what the user sees.
  const headerCounts = useMemo(() => {
    if (!filterActive) {
      return { open: data.open_count, done: data.completed_count };
    }
    let open = 0, done = 0;
    for (const t of visibleTasks) {
      if (t.is_informational) continue;
      if (t.completed) done += 1; else open += 1;
    }
    return { open, done };
  }, [filterActive, data.open_count, data.completed_count, visibleTasks]);

  // Group OPEN tasks by (year, month) for section headers.
  const groupedOpen = useMemo(() => {
    const map = new Map();
    for (const t of openTasks) {
      const key = `${t.due_year}-${t.due_month}`;
      if (!map.has(key)) map.set(key, { year: t.due_year, month: t.due_month, tasks: [] });
      map.get(key).tasks.push(t);
    }
    return Array.from(map.values()).sort((a, b) => {
      if (a.year !== b.year) return a.year - b.year;
      return a.month - b.month;
    });
  }, [openTasks]);

  const toggle = async (task) => {
    if (pending[task.key]) return;
    const next = !task.completed;
    setPending((p) => ({ ...p, [task.key]: true }));
    setData((d) => ({
      ...d,
      tasks: d.tasks.map((t) => (t.key === task.key ? { ...t, completed: next } : t)),
    }));
    try {
      if (next) await tasksApi.complete(task.key);
      else      await tasksApi.uncomplete(task.key);
      window.dispatchEvent(new Event('garden:tasks-changed'));
    } catch (e) {
      setData((d) => ({
        ...d,
        tasks: d.tasks.map((t) => (t.key === task.key ? { ...t, completed: !next } : t)),
      }));
      setError(e.message || 'Failed to update task');
    } finally {
      setPending((p) => {
        const copy = { ...p };
        delete copy[task.key];
        return copy;
      });
    }
  };

  const openCreateEditor = () => {
    setEditor({
      mode: 'create',
      id: null,
      label: '',
      notes: '',
      due_year: NOW_YEAR_MONTH.year,
      due_month: NOW_YEAR_MONTH.month,
      garden_plant_id: '',
    });
  };

  const openEditEditor = (task) => {
    setEditor({
      mode: 'edit',
      id: task.id,
      label: task.label || '',
      notes: task.notes || '',
      due_year: task.due_year || NOW_YEAR_MONTH.year,
      due_month: task.due_month || NOW_YEAR_MONTH.month,
      garden_plant_id: task.plant_id || '',
    });
  };

  // Create or patch the custom task. Optimistic update isn't worth the
  // complexity here — we just refetch after a successful save so month
  // grouping and completion state stay consistent.
  const saveEditor = async (draft) => {
    const body = {
      label: draft.label.trim(),
      notes: draft.notes.trim(),
      due_year: Number(draft.due_year),
      due_month: Number(draft.due_month),
      garden_plant_id: draft.garden_plant_id || null,
    };
    if (draft.mode === 'edit') {
      await tasksApi.updateCustom(draft.id, body);
    } else {
      await tasksApi.createCustom(body);
    }
    window.dispatchEvent(new Event('garden:tasks-changed'));
    await load(preset);
  };

  const deleteCustom = async (task) => {
    const ok = window.confirm(`Delete "${task.label}"?`);
    if (!ok) return;
    // Optimistic removal; restore on failure.
    const prev = data;
    setData((d) => ({ ...d, tasks: d.tasks.filter((t) => t.key !== task.key) }));
    try {
      await tasksApi.removeCustom(task.id);
      window.dispatchEvent(new Event('garden:tasks-changed'));
    } catch (e) {
      setData(prev);
      setError(e.message || 'Failed to delete task');
    }
  };

  const filterLabel = !filterActive
    ? 'All plants'
    : `${selectedPlantIds.length} of ${plants.length || selectedPlantIds.length} plants`;

  return (
    <div className="relative">
      <header className="px-4 pt-3 pb-2">
        <div className="flex items-center justify-between gap-2">
          <h1 className="text-lg font-semibold text-garden-ink">Tasks</h1>
          <div className="flex items-center gap-2">
            {!loading && data.tasks.length > 0 ? (
              <span className="text-xs text-garden-muted">
                {headerCounts.open} open{headerCounts.done ? ` \u00b7 ${headerCounts.done} done` : ''}
              </span>
            ) : null}
            <button
              type="button"
              onClick={openCreateEditor}
              className="inline-flex items-center gap-1 min-h-9 pl-2.5 pr-3 rounded-full bg-garden-primary text-white text-xs font-medium active:scale-95 transition"
              aria-label="Add task"
            >
              <Plus size={14} />
              <span>Add task</span>
            </button>
          </div>
        </div>
        <p className="mt-0.5 text-xs text-garden-muted">
          Auto-generated from your plant care details, plus any reminders you add.
        </p>
      </header>

      <div className="px-4 pb-2">
        <div
          role="tablist"
          className="inline-flex bg-white border border-garden-ink/10 rounded-full p-0.5 w-full max-w-full overflow-x-auto"
        >
          {PRESETS.map((p) => {
            const active = p.id === preset;
            return (
              <button
                key={p.id}
                role="tab"
                aria-selected={active}
                onClick={() => setPreset(p.id)}
                className={
                  'flex-1 whitespace-nowrap px-3 min-h-9 rounded-full text-xs font-medium transition ' +
                  (active
                    ? 'bg-garden-primary text-white shadow-sm'
                    : 'text-garden-muted')
                }
              >
                {p.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="px-4 pb-3 flex items-center gap-2 flex-wrap">
        {[
          { id: 'all',   label: 'All' },
          { id: 'todos', label: 'To-dos only' },
        ].map((opt) => {
          const active = showMode === opt.id;
          return (
            <button
              key={opt.id}
              onClick={() => setShowMode(opt.id)}
              className={
                'px-3 min-h-8 rounded-full text-[11px] font-medium border transition ' +
                (active
                  ? 'bg-garden-primary/10 border-garden-primary text-garden-primary'
                  : 'bg-white border-garden-ink/10 text-garden-muted')
              }
            >
              {opt.label}
            </button>
          );
        })}

        <button
          onClick={() => setFilterSheetOpen(true)}
          className={
            'min-h-8 pl-3 pr-2 rounded-full text-[11px] font-medium border inline-flex items-center gap-1 transition ' +
            (filterActive
              ? 'bg-garden-primary/10 border-garden-primary text-garden-primary'
              : 'bg-white border-garden-ink/10 text-garden-muted')
          }
        >
          <Filter size={12} />
          <span>{filterLabel}</span>
          {filterActive ? (
            <span
              role="button"
              tabIndex={0}
              aria-label="Clear plant filter"
              onClick={(e) => { e.stopPropagation(); setSelectedPlantIds([]); }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  e.stopPropagation();
                  setSelectedPlantIds([]);
                }
              }}
              className="ml-0.5 w-5 h-5 rounded-full flex items-center justify-center hover:bg-garden-primary/15"
            >
              <X size={12} />
            </span>
          ) : null}
        </button>
      </div>

      {error ? (
        <div className="mx-4 mb-3 p-3 rounded-xl bg-red-50 text-sm text-red-700">{error}</div>
      ) : null}

      {loading ? (
        <div className="px-4 py-6 text-sm text-garden-muted">Loading…</div>
      ) : data.tasks.length === 0 ? (
        <EmptyTasks
          onAddPlants={() => navigate('/garden/plants/new')}
          onAddTask={openCreateEditor}
        />
      ) : (
        <div className="pb-24">
          {openTasks.length === 0 ? (
            <div className="px-4 py-6 text-sm text-garden-muted">
              {filterActive
                ? <>Nothing open for the selected plants in this window. Try a wider date preset or clear the filter.</>
                : doneTasks.length > 0
                  ? <>All caught up! Expand <b>Done</b> below to see what you{'\u2019'}ve finished.</>
                  : <>Nothing to do right now. Switch to <b>All</b> to see seasonal events.</>}
            </div>
          ) : (
            groupedOpen.map(({ year, month, tasks }) => {
              const isCurrent = year === NOW_YEAR_MONTH.year && month === NOW_YEAR_MONTH.month;
              const openInMonth = tasks.filter((t) => !t.is_informational).length;
              return (
                <section key={`${year}-${month}`} className="mb-3">
                  <div
                    className={
                      'px-4 pt-2 pb-1 flex items-center gap-2 ' +
                      (isCurrent ? 'rounded-md mx-3 bg-garden-primary/5' : '')
                    }
                  >
                    <h2
                      className={
                        'text-xs uppercase tracking-wide font-semibold ' +
                        (isCurrent ? 'text-garden-primary' : 'text-garden-muted')
                      }
                    >
                      {isCurrent ? 'This month \u00b7 ' : ''}{MONTH_NAMES[month - 1]} {year}
                    </h2>
                    <span className="text-[11px] text-garden-muted/80">
                      {openInMonth} open
                    </span>
                  </div>
                  <ul
                    className={
                      'mx-3 bg-white rounded-2xl border divide-y divide-garden-ink/5 overflow-hidden ' +
                      (isCurrent ? 'border-garden-primary/30' : 'border-garden-ink/10')
                    }
                  >
                    {tasks.map((t) => (
                      <TaskRow
                        key={t.key}
                        task={t}
                        busy={!!pending[t.key]}
                        onToggle={() => toggle(t)}
                        onOpenPlant={t.plant_id
                          ? () => navigate(`/garden/plants/${t.plant_id}`)
                          : undefined}
                        onEdit={t.is_custom ? openEditEditor : undefined}
                        onDelete={t.is_custom ? deleteCustom : undefined}
                      />
                    ))}
                  </ul>
                </section>
              );
            })
          )}

          {doneTasks.length > 0 ? (
            <section className="mt-4 mb-3">
              <button
                type="button"
                onClick={() => setDoneOpen((v) => !v)}
                className="w-full px-4 pt-2 pb-1 flex items-center gap-2 text-left"
                aria-expanded={doneOpen}
              >
                {doneOpen
                  ? <ChevronDown size={14} className="text-garden-muted" />
                  : <ChevronRight size={14} className="text-garden-muted" />}
                <h2 className="text-xs uppercase tracking-wide font-semibold text-garden-muted">
                  Done
                </h2>
                <span className="text-[11px] text-garden-muted/80">
                  {doneTasks.length}
                </span>
              </button>
              {doneOpen ? (
                <ul className="mx-3 bg-white rounded-2xl border border-garden-ink/10 divide-y divide-garden-ink/5 overflow-hidden">
                  {doneTasks.map((t) => (
                    <TaskRow
                      key={t.key}
                      task={t}
                      busy={!!pending[t.key]}
                      onToggle={() => toggle(t)}
                      onOpenPlant={t.plant_id
                        ? () => navigate(`/garden/plants/${t.plant_id}`)
                        : undefined}
                      onEdit={t.is_custom ? openEditEditor : undefined}
                      onDelete={t.is_custom ? deleteCustom : undefined}
                    />
                  ))}
                </ul>
              ) : null}
            </section>
          ) : null}
        </div>
      )}

      <PlantFilterSheet
        open={filterSheetOpen}
        plants={plants}
        selected={selectedPlantIds}
        onChange={setSelectedPlantIds}
        onClose={() => setFilterSheetOpen(false)}
      />

      <CustomTaskEditor
        editor={editor}
        plants={plants}
        onSave={saveEditor}
        onClose={() => setEditor(null)}
      />
    </div>
  );
}

function EmptyTasks({ onAddPlants, onAddTask }) {
  return (
    <div className="px-6 py-12 flex flex-col items-center text-center">
      <div className="w-14 h-14 rounded-full bg-garden-primary/10 text-garden-primary flex items-center justify-center mb-3">
        <ListChecks size={28} />
      </div>
      <h2 className="text-lg font-semibold text-garden-ink">No tasks yet</h2>
      <p className="mt-2 text-sm text-garden-muted max-w-xs">
        Tasks appear automatically from your plants{'\u2019'} care details {'\u2014'} prune
        months, fertilise timing, seasonal reminders, and more. You can also add
        your own reminders.
      </p>
      <div className="mt-5 flex gap-2">
        <button
          onClick={onAddPlants}
          className="min-h-11 px-5 rounded-full bg-garden-primary text-white text-sm font-medium active:scale-95 transition"
        >
          Add a plant
        </button>
        <button
          onClick={onAddTask}
          className="min-h-11 px-5 rounded-full bg-white border border-garden-ink/15 text-garden-ink text-sm font-medium active:scale-95 transition"
        >
          Add a task
        </button>
      </div>
    </div>
  );
}

// ---- Custom task editor ----------------------------------------------------

function CustomTaskEditor({ editor, plants, onSave, onClose }) {
  const open = !!editor;
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (open) {
      setDraft(editor);
      setErr(null);
      setBusy(false);
    } else {
      setDraft(null);
    }
  }, [open, editor]);

  if (!open || !draft) return null;

  const set = (patch) => setDraft((d) => ({ ...d, ...patch }));

  const submit = async (e) => {
    e?.preventDefault?.();
    const label = (draft.label || '').trim();
    if (!label) { setErr('Name is required'); return; }
    setBusy(true);
    setErr(null);
    try {
      await onSave(draft);
      onClose();
    } catch (ex) {
      setErr(ex.message || 'Failed to save task');
    } finally {
      setBusy(false);
    }
  };

  const year = Number(draft.due_year) || NOW_YEAR_MONTH.year;
  // Year options: last year, this year, next year, year after. Covers the
  // three built-in windows without a date picker component.
  const yearOptions = [year - 1, NOW_YEAR_MONTH.year, NOW_YEAR_MONTH.year + 1, NOW_YEAR_MONTH.year + 2]
    .filter((v, i, arr) => arr.indexOf(v) === i)
    .sort((a, b) => a - b);

  return (
    <BottomSheet
      open={open}
      onClose={busy ? () => {} : onClose}
      title={draft.mode === 'edit' ? 'Edit task' : 'Add a task'}
      footer={
        <div className="flex items-center justify-end gap-2">
          <PrimaryButton variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </PrimaryButton>
          <PrimaryButton onClick={submit} disabled={busy}>
            {busy ? 'Saving…' : (draft.mode === 'edit' ? 'Save' : 'Add task')}
          </PrimaryButton>
        </div>
      }
    >
      <form onSubmit={submit} className="space-y-3">
        <label className="block">
          <span className="block text-xs font-medium text-garden-muted mb-1">
            What needs doing?
          </span>
          <input
            value={draft.label}
            onChange={(e) => set({ label: e.target.value })}
            placeholder="e.g. Pick up compost"
            autoFocus
            className="w-full min-h-11 px-3 rounded-xl border border-garden-ink/15 bg-white text-sm focus:outline-none focus:border-garden-primary"
          />
        </label>

        <label className="block">
          <span className="block text-xs font-medium text-garden-muted mb-1">
            Notes (optional)
          </span>
          <textarea
            value={draft.notes}
            onChange={(e) => set({ notes: e.target.value })}
            rows={3}
            placeholder="Any extra details"
            className="w-full px-3 py-2 rounded-xl border border-garden-ink/15 bg-white text-sm focus:outline-none focus:border-garden-primary"
          />
        </label>

        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="block text-xs font-medium text-garden-muted mb-1">Month</span>
            <select
              value={draft.due_month}
              onChange={(e) => set({ due_month: Number(e.target.value) })}
              className="w-full min-h-11 px-3 rounded-xl border border-garden-ink/15 bg-white text-sm focus:outline-none focus:border-garden-primary"
            >
              {MONTH_NAMES.map((name, i) => (
                <option key={name} value={i + 1}>{name}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="block text-xs font-medium text-garden-muted mb-1">Year</span>
            <select
              value={draft.due_year}
              onChange={(e) => set({ due_year: Number(e.target.value) })}
              className="w-full min-h-11 px-3 rounded-xl border border-garden-ink/15 bg-white text-sm focus:outline-none focus:border-garden-primary"
            >
              {yearOptions.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </label>
        </div>

        <label className="block">
          <span className="block text-xs font-medium text-garden-muted mb-1">
            Plant (optional)
          </span>
          <select
            value={draft.garden_plant_id || ''}
            onChange={(e) => set({ garden_plant_id: e.target.value })}
            className="w-full min-h-11 px-3 rounded-xl border border-garden-ink/15 bg-white text-sm focus:outline-none focus:border-garden-primary"
          >
            <option value="">{'\u2014'} No specific plant {'\u2014'}</option>
            {plants.map((p) => (
              <option key={p.id} value={p.id}>
                {displayPrimaryPlantName(p)}
              </option>
            ))}
          </select>
        </label>

        {err ? (
          <div className="p-3 rounded-xl bg-red-50 text-sm text-red-700">{err}</div>
        ) : null}
      </form>
    </BottomSheet>
  );
}

// ---- Plant filter sheet ----------------------------------------------------

function PlantFilterSheet({ open, plants, selected, onChange, onClose }) {
  // Local draft selection so the user can cancel without persisting partial
  // choices. Reset whenever the sheet is (re)opened.
  const [draft, setDraft] = useState(selected);
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (open) {
      setDraft(selected);
      setQuery('');
    }
  }, [open, selected]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return plants;
    return plants.filter((p) => {
      const common = (p.common_name || '').toLowerCase();
      const bot = (p.botanical_name || '').toLowerCase();
      return common.includes(q) || bot.includes(q);
    });
  }, [plants, query]);

  const toggleOne = (id) => {
    setDraft((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const selectAllVisible = () => {
    const ids = new Set(draft);
    filtered.forEach((p) => ids.add(p.id));
    setDraft(Array.from(ids));
  };

  const clearAll = () => setDraft([]);

  const apply = () => {
    onChange(draft);
    onClose();
  };

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      title="Filter by plant"
      footer={
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs text-garden-muted">
            {draft.length === 0 ? 'All plants' : `${draft.length} selected`}
          </span>
          <div className="flex gap-2">
            <PrimaryButton variant="secondary" onClick={onClose}>Cancel</PrimaryButton>
            <PrimaryButton onClick={apply}>Done</PrimaryButton>
          </div>
        </div>
      }
    >
      <div className="space-y-3">
        <div className="relative">
          <Search
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-garden-muted"
          />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search plants"
            className="w-full min-h-10 pl-9 pr-3 rounded-full border border-garden-ink/15 bg-white text-sm focus:outline-none focus:border-garden-primary"
          />
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={selectAllVisible}
            disabled={filtered.length === 0}
            className="text-xs font-medium text-garden-primary disabled:opacity-40"
          >
            Select all{query ? ' (visible)' : ''}
          </button>
          <span className="text-garden-ink/20">|</span>
          <button
            onClick={clearAll}
            disabled={draft.length === 0}
            className="text-xs font-medium text-garden-muted disabled:opacity-40"
          >
            Clear
          </button>
        </div>

        {plants.length === 0 ? (
          <div className="text-sm text-garden-muted py-6 text-center">
            You haven{'\u2019'}t added any plants yet.
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-sm text-garden-muted py-6 text-center">
            No plants match {'\u201c'}{query}{'\u201d'}.
          </div>
        ) : (
          <ul className="divide-y divide-garden-ink/5">
            {filtered.map((p) => {
              const on = draft.includes(p.id);
              const botSub = displayBotanicalSubtitle(p);
              return (
                <li key={p.id}>
                  <button
                    onClick={() => toggleOne(p.id)}
                    className="w-full flex items-center gap-3 py-2 min-h-11 text-left"
                    aria-pressed={on}
                  >
                    <span className="w-10 h-10 rounded-full bg-garden-primaryLight flex items-center justify-center overflow-hidden shrink-0">
                      {p.thumbnail_url ? (
                        <img
                          src={p.thumbnail_url}
                          alt=""
                          className="w-full h-full object-cover"
                          draggable={false}
                        />
                      ) : (
                        <span aria-hidden>{'\u{1F33F}'}</span>
                      )}
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="block font-medium text-garden-ink truncate">
                        {displayPrimaryPlantName(p)}
                      </span>
                      {botSub ? (
                        <span className="block text-xs italic text-garden-muted truncate">
                          {botSub}
                        </span>
                      ) : null}
                    </span>
                    <span
                      className={
                        'shrink-0 w-5 h-5 rounded-md border flex items-center justify-center ' +
                        (on
                          ? 'bg-garden-primary border-garden-primary text-white'
                          : 'border-garden-ink/25 bg-white')
                      }
                      aria-hidden
                    >
                      {on ? (
                        <svg viewBox="0 0 16 16" width="12" height="12" fill="none">
                          <path d="M3 8.5l3 3 7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      ) : null}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </BottomSheet>
  );
}
